import { FunctionComponent } from "react";
import { Control, Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";

import {
	LastContactPreset,
	REENGAGE_FIELDS,
	SENTIMENT_OPTIONS,
} from "../constants/reengage.constants";
import { ReengageSourceFormValues } from "../schema/reengageSource";
import { LastContactSelect } from "./LastContactSelect";
import { ReengageDropdownField } from "./ReengageDropdownField";

interface ReengagementCriteriaCardProps {
	control: Control<ReengageSourceFormValues>;
	listId?: number;
	lastContactError?: boolean;
}

const SubcardHeader: FunctionComponent<{ title: string; sub: string }> = ({ title, sub }) => (
	<div className="flex items-center gap-2 mb-3.5">
		<span className="text-[13px] font-semibold text-text">{title}</span>
		<span className="ml-auto text-xs text-text-meta">{sub}</span>
	</div>
);

export const ReengagementCriteriaCard: FunctionComponent<ReengagementCriteriaCardProps> = ({
	control,
	listId,
	lastContactError,
}) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`list.reengage.${key}`);

	return (
		<div className="bg-background-light border border-border rounded-lg p-4 mb-4">
			<SubcardHeader sub={ts("criteriaSub")} title={ts("criteriaTitle")} />

			<ReengageDropdownField
				control={control}
				label={ts("fromList")}
				listId={listId}
				name="fromLists"
				placeholder={ts("allLists")}
				propertyName={REENGAGE_FIELDS.fromList.name}
			/>

			<ReengageDropdownField
				control={control}
				label={ts("bestSentiment")}
				name="bestSentiment"
				placeholder={ts("anySentiment")}
				staticOptions={SENTIMENT_OPTIONS}
			/>

			<div className="mb-4">
				<div className="flex items-center justify-between mb-1.5">
					<span className="text-[13px] font-medium text-text">{ts("lastContact")}</span>
					<span className="text-[11px] text-text-meta">{ts("singleSelect")}</span>
				</div>
				<Controller
					control={control}
					name="lastContact"
					render={({ field: presetField }) => (
						<Controller
							control={control}
							name="lastContactRange"
							render={({ field: rangeField }) => (
								<LastContactSelect
									error={lastContactError}
									range={rangeField.value}
									value={presetField.value as LastContactPreset}
									onChange={presetField.onChange}
									onRangeChange={rangeField.onChange}
								/>
							)}
						/>
					)}
				/>
			</div>
		</div>
	);
};
